import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { VoteValue } from 'src/api/types';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { gTCheckHandler } from 'src/components/GenericTable/util';
import { Link } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { RankIndicator, TextOverflowTooltip } from 'src/common';
import { Rating } from './comps';
import _ from 'lodash';
import { ControlsRmpQuery } from 'src/graphql/_generated';
import { isControlLinked } from './util';

type RankedControlData = {
  ux_id?: string;
  name: string;
  description?: string | null;
  final_score?: number;
  user_vote?: VoteValue;
  id?: string;
  controlId?: string | null;
  controlLibraryId?: string | null;
  controlLibrary_Id?: string | null;
  frameworkLibrary_shortname?: string | null;
  control_identifier?: string | null;
  frameworkShortname?: string | null;
  FrameworkAuthor?: string | null;
  shortname?: string | null;
  strategy?: any;
  status?: any;
};

type UseColumns = (a: {
  data: RankedControlData[];
  controlsRmp: ControlsRmpQuery['controls'];
  scenarioId: string;
  checked?: string[];
  setChecked?: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  columns: ColumnDef<RankedControlData>[];
  GTColumnSelectAnchorExported: JSX.Element;
  checked: string[];
  resetChecked: () => void;
};

const defaultColumns = ['frameworkShortname', 'control_identifier', 'name', 'FrameworkAuthor', 'final_score'];

export const useColumns: UseColumns = ({
  data = [],
  controlsRmp,
  scenarioId,
  checked: externalChecked,
  setChecked: externalSetChecked,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.scenarioLinkSuggestedControl}-columns`,
    defaultColumns,
  );

  const [internalChecked, setInternalChecked] = useState<string[]>([]);
  const checked = externalChecked ?? internalChecked;
  const setChecked = externalSetChecked ?? setInternalChecked;

  const min = _.min(data.map((item) => item.final_score || 0)) ?? 0;
  const max = _.max(data.map((item) => item.final_score || 0)) ?? 500;

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<RankedControlData>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler, visibleRows } = gTCheckHandler(
            {
              checked,
              selector: ['id', 'ux_id'],
              table,
            },
            setChecked,
          );

          // Only consider rows that are actually linked (not just selected)
          const linkedRows = visibleRows.filter((el) => {
            const row = data.find((item) => (item.id || item.ux_id) === el);
            if (!row) return false;
            const controlId = row.id || row.ux_id;
            if (!controlId) return false;
            return isControlLinked({
              controlId,
              controlsRmp,
              scenarioId,
              control_identifier: row.controlLibraryId,
            });
          });

          // All visible rows are actually linked (not just selected)
          const isAllLinked = visibleRows.length > 0 && linkedRows.length === visibleRows.length;

          // Filter out already linked rows from the check handler logic
          const selectableRows = visibleRows.filter((el) => !linkedRows.includes(el));
          const isAllSelectableChecked =
            selectableRows.length > 0 && selectableRows.every((el) => checked.includes(el));

          return (
            <GTCheckBox
              isChecked={isAllSelectableChecked || isAllLinked}
              disabled={isAllLinked}
              onClick={() => {
                if (isAllLinked) return; // Don't handle clicks when all are linked

                // Only toggle selectable (non-linked) rows
                if (isAllSelectableChecked) {
                  setChecked(checked.filter((id) => !selectableRows.includes(id)));
                } else {
                  const newChecked = [...checked];
                  selectableRows.forEach((id) => {
                    if (!newChecked.includes(id)) {
                      newChecked.push(id);
                    }
                  });
                  setChecked(newChecked);
                }
              }}
            />
          );
        },
        cell: ({ row }) => {
          const id = row.original.id || row.original.ux_id;

          if (!id) return null;

          const isLinked = isControlLinked({
            controlId: id,
            controlsRmp,
            scenarioId,
            control_identifier: row.original.controlLibraryId || row.original.control_identifier,
          });

          return (
            <GTCheckBox
              isChecked={checked.includes(id) || isLinked}
              disabled={isLinked}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || row.ux_id,
        cell: (info) => info.getValue(),
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        accessorFn: (row) => row.frameworkShortname || row.frameworkLibrary_shortname,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name || null,
        cell: (info) => {
          // @ts-ignore
          const isRmp = !!info.row.original.id;
          return (
            <TextOverflowTooltip>
              <Link
                className="table-link"
                to={
                  isRmp
                    ? `/risk-management/control/${info.row.original.id}`
                    : `/control-frameworks/all-controls/${info.row.original.id || info.row.original.ux_id}`
                }
                target={'_blank'}
                onClick={(e) => {
                  e.stopPropagation();
                  mpEvent(MPEvents.Link, {
                    source: {
                      value: ['Framework'],
                    },
                    destination: {
                      value: ['Framework Controls all'],
                      params: {
                        id: info.row.original.id || info.row.original.ux_id || info.row.original.controlLibrary_Id,
                      },
                    },
                    tags: ['FRAMEWORK'],
                  });
                }}
              >
                {info.getValue() as string}
              </Link>
            </TextOverflowTooltip>
          );
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'FrameworkAuthor',
        header: 'Author',
        accessorFn: (row) => row.FrameworkAuthor || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'final_score',
        header: 'Score',
        accessorFn: (row) => row.final_score || 0,
        cell: (info) => {
          return <RankIndicator min={min} max={max} score={info.getValue() as number} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                extraComponentLeft={
                  <Rating
                    value={row.original.user_vote || null}
                    controlId={row.original.id || row.original.ux_id || row.original.controlLibrary_Id || ''}
                  />
                }
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked, controlsRmp, scenarioId, min, max]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.scenarioLinkSuggestedControl}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'name', title: 'Name' },
        { key: 'FrameworkAuthor', title: 'Author' },
        { key: 'final_score', title: 'Score' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    checked,
    resetChecked: () => setChecked([]),
  };
};
